import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, RefreshCw, Copy, Check, Loader2, Camera, Newspaper, Clapperboard, MessageSquareQuote, Moon, BarChart3, Heart } from "lucide-react";
import { getStorieDay, regenStorieDay, regenStorieSlot, storieDm, patchStorieSlot, getStorieHistory } from "@/lib/api";

const ICONS = { mattina: Camera, letto: Newspaper, reel: Clapperboard, dm: MessageSquareQuote, sera: Moon };
const STICKER = {
  sondaggio: { nome: "Sondaggio", come: "sticker «Sondaggio»: scrivi la domanda e le 2 risposte" },
  domanda: { nome: "Domande", come: "sticker «Domande»: scrivi la domanda, loro rispondono in privato" },
  slider: { nome: "Slider emoji", come: "nel menu sticker è quello A DESTRA di «Sondaggio», con la faccina e la barretta (senza nome): scrivi la domanda, tocca la faccina per cambiarla" },
};

const shift = (date, n) => {
  const d = new Date(`${date}T12:00:00`);
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
};

const CopyBtn = ({ text, testid }) => {
  const [ok, setOk] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(text); setOk(true); setTimeout(() => setOk(false), 1200); } catch { toast.error("Copia non riuscita"); }
  };
  return (
    <button onClick={copy} data-testid={testid} title="Copia" className="shrink-0 p-1.5 rounded-md border border-[#262626] text-neutral-400 hover:text-white hover:border-[#404040] transition-colors">
      {ok ? <Check size={14} className="text-emerald-400" /> : <Copy size={14} />}
    </button>
  );
};

const Step = ({ n, label, children }) => (
  <div className="mt-3 flex gap-3">
    <span className="shrink-0 w-5 h-5 rounded-full bg-[#D92D20] text-white text-[0.65rem] font-bold flex items-center justify-center mt-0.5">{n}</span>
    <div className="flex-1 min-w-0">
      <p className="text-[0.65rem] uppercase tracking-[0.2em] text-neutral-500 mb-1">{label}</p>
      {children}
    </div>
  </div>
);

const Sticker = ({ s, n }) => {
  if (!s || s.tipo === "nessuno") return <Step n={n} label="Sticker"><p className="text-sm text-neutral-500">Nessuno: solo foto e testo.</p></Step>;
  const info = STICKER[s.tipo] || { nome: s.tipo, come: "" };
  return (
    <Step n={n} label={`Aggiungi lo sticker · ${info.nome}`}>
      <div data-testid="storia-sticker">
        {s.domanda && <p className="text-sm text-white">Domanda da scrivere: <span className="font-semibold">«{s.domanda}»</span></p>}
        {s.tipo === "slider" && (
          <p className="mt-1.5 text-sm text-neutral-300">Faccina del cursore: <span className="text-xl align-middle">{s.emoji || (s.opzioni || [])[0] || "😮‍💨"}</span> <span className="text-xs text-neutral-500">— niente risposte da scrivere: la gente trascina la faccina da «poco» a «tanto»</span></p>
        )}
        {s.tipo !== "slider" && (s.opzioni || []).filter(Boolean).length > 0 && (
          <div className="mt-1.5 flex flex-wrap items-center gap-2 text-xs">
            <span className="text-neutral-500">Le 2 risposte:</span>
            {(s.opzioni || []).filter(Boolean).map((o, i) => (
              <span key={i} className="px-2 py-0.5 rounded-full border border-[#D92D20]/40 text-neutral-200">{o}</span>
            ))}
          </div>
        )}
        <p className="mt-1.5 text-xs text-neutral-500">{info.come}</p>
      </div>
    </Step>
  );
};

function DmBox({ date, slot, onUpdate }) {
  const [testo, setTesto] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    if (testo.trim().length < 5) return toast.error("Incolla il messaggio ricevuto");
    setBusy(true);
    try { onUpdate(await storieDm(date, testo)); setTesto(""); toast.success("Risposta pronta"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    setBusy(false);
  };
  return (
    <div className="mt-3 space-y-2" data-testid="storia-dm-box">
      {slot.placeholder && (
        <p className="text-xs text-amber-400/90">Nessun DM incollato: sotto c'è il commento contrario più probabile. Incolla quello vero quando arriva.</p>
      )}
      {slot.commento && (
        <blockquote className="border-l-2 border-[#404040] pl-3 text-sm text-neutral-400 italic" data-testid="storia-dm-commento">«{slot.commento}»</blockquote>
      )}
      <textarea value={testo} onChange={(e) => setTesto(e.target.value)} rows={2} placeholder="Incolla qui il DM o il commento che ti ha colpito…"
        data-testid="storia-dm-input" className="w-full bg-[#0a0a0a] border border-[#262626] rounded-md px-3 py-2 text-sm text-white placeholder:text-neutral-600 outline-none focus:border-[#D92D20]" />
      <button onClick={send} disabled={busy} data-testid="storia-dm-submit"
        className="inline-flex items-center gap-2 text-xs font-semibold px-3 py-1.5 rounded-md bg-[#D92D20] hover:bg-[#b82418] disabled:opacity-50 text-white transition-colors">
        {busy ? <Loader2 size={13} className="animate-spin" /> : <MessageSquareQuote size={13} />} Scrivimi la risposta
      </button>
    </div>
  );
}

function StoriaCard({ date, slot, onUpdate }) {
  const Icon = ICONS[slot.tipo] || Camera;
  const [risposte, setRisposte] = useState(slot.risposte ?? "");
  const [regen, setRegen] = useState(false);
  const patch = async (body) => {
    try { onUpdate(await patchStorieSlot(date, slot.id, body)); } catch { toast.error("Salvataggio non riuscito"); }
  };
  const rifai = async () => {
    setRegen(true);
    try { onUpdate(await regenStorieSlot(date, slot.id)); toast.success("Storia rifatta"); } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    setRegen(false);
  };
  return (
    <div className={`rounded-lg border p-5 transition-colors ${slot.fatto ? "border-emerald-900/60 bg-[#0c130f]" : "border-[#262626] bg-[#111]"}`} data-testid={`storia-${slot.tipo}`}>
      <div className="flex items-start gap-4">
        <div className="shrink-0 text-center w-14">
          <p className="font-display font-extrabold text-lg leading-none text-white">{slot.ora}</p>
          <Icon size={16} className="mx-auto mt-2 text-[#D92D20]" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-3">
            <h3 className="font-display font-bold text-base text-white leading-tight">{slot.titolo}
              {slot.registro && (
                <span data-testid={`storia-${slot.tipo}-registro`} className={`ml-2 align-middle inline-flex items-center gap-1 text-[0.6rem] uppercase tracking-[0.15em] px-2 py-0.5 rounded-full border ${slot.registro === "cuore" ? "border-rose-700/60 text-rose-300" : "border-[#404040] text-neutral-400"}`}>
                  {slot.registro === "cuore" ? <><Heart size={10} /> Cuore</> : "Pressione"}
                </span>
              )}
            </h3>
            <div className="flex items-center gap-3 shrink-0">
              <button onClick={rifai} disabled={regen || slot.fatto} data-testid={`storia-${slot.tipo}-rifai`} title="Rifai solo questa storia"
                className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-md border border-[#262626] text-neutral-400 hover:text-white hover:border-[#404040] disabled:opacity-40 transition-colors">
                <RefreshCw size={12} className={regen ? "animate-spin" : ""} /> {regen ? "Rifaccio…" : "Rifai questa"}
              </button>
              <label className="flex items-center gap-2 text-xs text-neutral-400 cursor-pointer">
                <input type="checkbox" checked={!!slot.fatto} onChange={(e) => patch({ fatto: e.target.checked })} data-testid={`storia-${slot.tipo}-fatta`} className="w-4 h-4 accent-[#D92D20]" />
                fatta
              </label>
            </div>
          </div>
          <Step n={1} label={slot.tipo === "dm" ? "Fai lo screenshot" : slot.tipo === "reel" ? "Condividi il reel" : slot.tipo === "letto" ? "Fai lo screenshot della notizia" : "Inquadra"}>
            <p className="text-sm text-neutral-300">{slot.cosa_inquadrare}</p>
          </Step>

          {slot.tipo === "letto" ? (
            <>
              <Step n={2} label="Scrivi sopra lo screenshot (la tua opinione, 2 righe)">
                <div className="flex items-start gap-2">
                  <p className="text-lg font-semibold text-white leading-snug flex-1" data-testid={`storia-${slot.tipo}-testo`}>{slot.opinione || slot.testo_schermo}</p>
                  <CopyBtn text={slot.opinione || slot.testo_schermo} testid={`storia-${slot.tipo}-copy`} />
                </div>
                {slot.opinione && slot.testo_schermo && (
                  <p className="mt-2 text-xs text-neutral-500">Se c'è spazio, in alto una riga di titolo: <span className="text-neutral-300">{slot.testo_schermo}</span></p>
                )}
              </Step>
              <Sticker s={slot.sticker} n={3} />
            </>
          ) : slot.tipo === "dm" ? (
            <>
              <Step n={2} label="Scrivi in alto (una riga)">
                <div className="flex items-start gap-2">
                  <p className="text-sm text-white flex-1">{slot.testo_schermo || "—"}</p>
                  {slot.testo_schermo && <CopyBtn text={slot.testo_schermo} testid={`storia-${slot.tipo}-copy-riga`} />}
                </div>
              </Step>
              <Step n={3} label="Scrivi sotto lo screenshot: la tua risposta">
                <div className="flex items-start gap-2">
                  <p className="text-lg font-semibold text-white leading-snug flex-1" data-testid={`storia-${slot.tipo}-testo`}>{slot.risposta}</p>
                  <CopyBtn text={slot.risposta} testid={`storia-${slot.tipo}-copy`} />
                </div>
              </Step>
            </>
          ) : (
            <>
              <Step n={2} label="Scrivi sullo schermo (una riga, in alto)">
                <div className="flex items-start gap-2">
                  <p className="text-lg font-semibold text-white leading-snug flex-1" data-testid={`storia-${slot.tipo}-testo`}>{slot.testo_schermo}</p>
                  <CopyBtn text={slot.testo_schermo} testid={`storia-${slot.tipo}-copy`} />
                </div>
              </Step>
              <Sticker s={slot.sticker} n={3} />
            </>
          )}
          {slot.tipo === "dm" && <DmBox date={date} slot={slot} onUpdate={onUpdate} />}
          {slot.nota && <p className="mt-3 text-xs text-neutral-500 italic">{slot.nota}</p>}
          {slot.fatto && (
            <div className="mt-3 flex items-center gap-2 text-xs text-neutral-400">
              <span>Risposte ricevute:</span>
              <input type="number" min="0" value={risposte} onChange={(e) => setRisposte(e.target.value)}
                onBlur={() => risposte !== "" && patch({ risposte: Number(risposte) })}
                data-testid={`storia-${slot.tipo}-risposte`} className="w-16 bg-[#0a0a0a] border border-[#262626] rounded px-2 py-1 text-white text-xs outline-none focus:border-[#D92D20]" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function CosaFunziona() {
  const [h, setH] = useState(null);
  useEffect(() => { getStorieHistory(14).then(setH).catch(() => setH(null)); }, []);
  if (!h || !h.per_tipo?.length) return null;
  return (
    <div className="rounded-lg border border-[#262626] bg-[#111] p-5" data-testid="storie-stats">
      <div className="flex items-center gap-2 mb-3"><BarChart3 size={15} className="text-[#D92D20]" /><h3 className="font-display font-bold text-sm text-white">Cosa funziona (ultimi 14 giorni)</h3></div>
      <p className="text-xs text-neutral-500 mb-3">{h.fatte} storie fatte su {h.totale} in scaletta · {h.giorni} giorni</p>
      <ul className="space-y-1.5">
        {h.per_tipo.map((a) => (
          <li key={a.tipo} className="flex items-center justify-between text-sm">
            <span className="text-neutral-300">{a.ora} · {a.tipo}</span>
            <span className="text-neutral-400">{a.media} risposte in media <span className="text-neutral-600">({a.storie})</span></span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function StorieOggi() {
  const [date, setDate] = useState(() => new Date().toLocaleDateString("sv-SE", { timeZone: "Europe/Rome" }));
  const [day, setDay] = useState(null);
  const [loading, setLoading] = useState(false);
  const [regen, setRegen] = useState(false);

  const load = useCallback(async (d) => {
    setLoading(true); setDay(null);
    try { setDay(await getStorieDay(d)); } catch (e) { toast.error(e?.response?.data?.detail || "Errore nel caricamento"); }
    setLoading(false);
  }, []);
  useEffect(() => { load(date); }, [date, load]);

  const rigenera = async () => {
    setRegen(true);
    try { setDay(await regenStorieDay(date)); toast.success("Scaletta rifatta"); } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    setRegen(false);
  };

  const fatte = day?.slots?.filter((s) => s.fatto).length || 0;

  return (
    <div className="grid lg:grid-cols-[1fr_320px] gap-8" data-testid="storie-page">
      <div>
        <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
          <div>
            <p className="text-[0.65rem] uppercase tracking-[0.25em] text-neutral-500 mb-1">Storie di oggi · 5 al giorno, zero vendita</p>
            <div className="flex items-center gap-2">
              <button onClick={() => setDate(shift(date, -1))} data-testid="storie-prev" className="p-1.5 rounded-md border border-[#262626] text-neutral-400 hover:text-white"><ChevronLeft size={16} /></button>
              <h2 className="font-display font-extrabold text-2xl text-white capitalize" data-testid="storie-label">{day?.label || date}</h2>
              <button onClick={() => setDate(shift(date, 1))} data-testid="storie-next" className="p-1.5 rounded-md border border-[#262626] text-neutral-400 hover:text-white"><ChevronRight size={16} /></button>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {day && <span className="text-sm text-neutral-400" data-testid="storie-progress">{fatte}/5 fatte</span>}
            <button onClick={rigenera} disabled={regen || loading} data-testid="storie-regen"
              className="inline-flex items-center gap-2 text-xs px-2 py-1.5 rounded-md text-neutral-500 hover:text-neutral-200 disabled:opacity-50 transition-colors">
              <RefreshCw size={12} className={regen ? "animate-spin" : ""} /> Rifai tutta la giornata
            </button>
          </div>
        </div>

        {loading && (
          <div className="flex items-center gap-3 text-neutral-400 text-sm py-10" data-testid="storie-loading">
            <Loader2 size={16} className="animate-spin" /> Preparo la scaletta: reel del giorno, notizia, vita vera…
          </div>
        )}

        {day && (
          <div className="space-y-3">
            {day.slots.map((s) => <StoriaCard key={s.id} date={date} slot={s} onUpdate={setDay} />)}
          </div>
        )}
      </div>

      <aside className="space-y-4">
        {day?.contesto && (
          <div className="rounded-lg border border-[#262626] bg-[#111] p-5 text-sm" data-testid="storie-contesto">
            <h3 className="font-display font-bold text-sm text-white mb-3">Da cosa è costruita</h3>
            <p className="text-neutral-400"><span className="text-neutral-500">Reel del giorno:</span> {day.contesto.reel_titolo ? day.contesto.reel_titolo.replace(/^14:30 · /, "").slice(0, 110) : "non in calendario"}</p>
            <p className="text-neutral-400 mt-2"><span className="text-neutral-500">Notizia:</span> {day.contesto.news_title || "nessuna salvata"}
              {day.contesto.news_link && <a href={day.contesto.news_link} target="_blank" rel="noreferrer" className="ml-1 text-[#D92D20] underline" data-testid="storie-news-link">apri</a>}
            </p>
            <p className="text-neutral-400 mt-2"><span className="text-neutral-500">Domani:</span> {day.contesto.prossimo ? day.contesto.prossimo.replace(/^14:30 · /, "").slice(0, 90) : "non ancora in piano"}</p>
          </div>
        )}
        <div className="rounded-lg border border-[#262626] bg-[#111] p-5 text-xs text-neutral-400 leading-relaxed" data-testid="storie-come-si-legge">
          <h3 className="font-display font-bold text-sm text-white mb-2">Come si legge una card</h3>
          <p><span className="text-white font-semibold">1</span> cosa fotografi o riprendi (max 10") · <span className="text-white font-semibold">2</span> il testo da scrivere sullo schermo (tasto copia) · <span className="text-white font-semibold">3</span> lo sticker da aggiungere, con la domanda già pronta.</p>
          <p className="mt-2"><span className="text-neutral-200">Sondaggio</span> = domanda + 2 risposte. <span className="text-neutral-200">Slider emoji</span> = nel menu sticker è a destra di «Sondaggio» (faccina + barretta, senza nome): scrivi solo la domanda, niente risposte. <span className="text-neutral-200">Domande</span> = il box dove ti rispondono in privato.</p>
        </div>
        <div className="rounded-lg border border-[#262626] bg-[#111] p-5 text-xs text-neutral-400 leading-relaxed" data-testid="storie-regole">
          <h3 className="font-display font-bold text-sm text-white mb-2">Le regole</h3>
          Sei uno di loro che dice la sua, non uno che ce l'ha fatta. Il risultato si vede, non si dice. Mai «grato», «libertà», «ho cambiato strada». Mai workshop, mai vendita. Foto o video di 10" di quello che c'è davvero: zero riprese fatte.
          <p className="mt-2"><span className="text-rose-300">Cuore</span> = padre e marito, e basta: niente soldi, niente sistema, niente teaser. <span className="text-neutral-200">Pressione</span> = la scena di casa che si aggancia ai temi. Ogni giorno una e una, si alternano tra mattina e sera.</p>
        </div>
        <CosaFunziona />
      </aside>
    </div>
  );
}
